import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDateTime } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Receipt, FilePdf, Image as ImageIcon, CheckCircle, XCircle, Clock, ArrowsClockwise } from "@phosphor-icons/react";

const STATUS = {
  pending: { label: "Pendente", cls: "medio", Icon: Clock },
  approved: { label: "Aprovado", cls: "green", Icon: CheckCircle },
  rejected: { label: "Rejeitado", cls: "red", Icon: XCircle },
};

const brl = (v) => (v == null ? "-" : ("R$ " + Number(v).toFixed(2)).replace(".", ","));
const brDate = (iso) => { try { return new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString("pt-BR"); } catch { return iso; } };

function ReceiptMedia({ r }) {
  const src = r.media_base64 || r.media_url;
  if (r.media_type === "image" && src) {
    return <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="comprovante" className="w-full max-h-48 object-contain border border-[#EBE5DF] bg-white" /></a>;
  }
  if (src) {
    return (
      <a href={src} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-[#EBE5DF] bg-[#FAF7F3] p-4 hover:bg-[#F5F0EB] transition-colors" data-testid="receipt-doc-link">
        <FilePdf size={28} weight="duotone" className="text-[#B4564F]" />
        <span className="text-sm underline truncate">{r.media_filename || "Comprovante (PDF)"}</span>
      </a>
    );
  }
  return <div className="flex items-center gap-2 text-xs text-[#5C5A57] border border-dashed border-[#EBE5DF] p-4"><ImageIcon size={18} /> Midia indisponivel</div>;
}

export default function ComprovantesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [status, setStatus] = useState("pending");
  const [resellerId, setResellerId] = useState("");
  const [resellers, setResellers] = useState([]);
  const [busy, setBusy] = useState("");
  const [openSettle, setOpenSettle] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [chosen, setChosen] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (isAdmin && resellerId) params.reseller_id = resellerId;
      const { data } = await api.get("/whatsapp/receipts", { params });
      setItems(data);
    } catch { toast.error("Erro ao carregar comprovantes"); }
    try { setStats((await api.get("/whatsapp/receipts/stats")).data); } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, resellerId]);
  useEffect(() => { if (isAdmin) api.get("/whatsapp/resellers").then(({ data }) => setResellers(data)).catch(() => {}); }, [isAdmin]);

  const resellerName = (id) => resellers.find((x) => x.id === id)?.name || "";

  const openSettleFor = async (r) => {
    setOpenSettle(r.id); setChosen(r.probable_installment_id || ""); setInstallments([]);
    try {
      const { data } = await api.get("/installments", { params: { client_id: r.client_id } });
      setInstallments(data.filter((i) => i.status !== "paid"));
    } catch { toast.error("Erro ao carregar parcelas"); }
  };

  const settle = async (r) => {
    if (!chosen) return toast.error("Selecione a parcela");
    setBusy(r.id);
    try {
      await api.post(`/whatsapp/receipts/${r.id}/settle`, { installment_id: chosen });
      toast.success("Baixa realizada e comprovante aprovado");
      setOpenSettle(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao dar baixa"); }
    finally { setBusy(""); }
  };

  const reject = async (r) => {
    setBusy(r.id);
    try { await api.post(`/whatsapp/receipts/${r.id}/reject`); toast.success("Comprovante rejeitado"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao rejeitar"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="comprovantes-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Financeiro</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Comprovantes</h1>
        </div>
        <Receipt size={42} weight="duotone" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="receipts-filters">
        {[["pending", `Pendentes (${stats.pending})`], ["approved", `Aprovados (${stats.approved})`], ["rejected", `Rejeitados (${stats.rejected})`], ["", "Todos"]].map(([k, label]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`receipts-filter-${k || "all"}`}
            className={`text-xs tracking-widest uppercase px-3 py-1.5 border transition-colors ${status === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#EBE5DF] hover:bg-[#FAF7F3]"}`}>
            {label}
          </button>
        ))}
        {isAdmin && (
          <select value={resellerId} onChange={(e) => setResellerId(e.target.value)} className="text-xs border border-[#EBE5DF] px-2 py-1.5 ml-auto" data-testid="receipts-reseller-filter">
            <option value="">Todas as revendedoras</option>
            {resellers.map((rv) => <option key={rv.id} value={rv.id}>{rv.name}</option>)}
          </select>
        )}
        <button onClick={load} className="text-xs tracking-widest uppercase font-medium hover:underline flex items-center gap-1" data-testid="receipts-refresh-btn"><ArrowsClockwise size={14} /> Atualizar</button>
      </div>

      {loading ? (
        <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="receipts-loading">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#EBE5DF] bg-[#FAF7F3] p-12 text-center text-sm text-[#5C5A57]" data-testid="receipts-empty">
          <Receipt size={36} weight="duotone" className="mx-auto mb-2" />
          Nenhum comprovante {status === "pending" ? "pendente" : status === "approved" ? "aprovado" : status === "rejected" ? "rejeitado" : ""}. Comprovantes (imagem/PDF) enviados pelas clientes aparecem aqui automaticamente.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((r) => {
            const S = STATUS[r.status] || STATUS.pending;
            const SIcon = S.Icon;
            return (
              <div key={r.id} className="border border-[#EBE5DF] bg-white p-4 flex flex-col gap-3" data-testid={`receipt-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {r.client_id
                      ? <Link to={`${isAdmin ? "/admin/clientes" : "/revenda/clientes"}/${r.client_id}`} className="font-display text-lg font-bold truncate hover:underline block" data-testid={`receipt-client-link-${r.id}`}>{r.client_name || r.phone}</Link>
                      : <div className="font-display text-lg font-bold truncate">{r.client_name || r.phone}</div>}
                    {isAdmin && <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">{resellerName(r.reseller_id) || "—"}</div>}
                  </div>
                  <span className={`badge ${S.cls} inline-flex items-center gap-1 shrink-0`}><SIcon size={12} weight="fill" /> {S.label}</span>
                </div>

                <ReceiptMedia r={r} />
                {r.caption && <div className="text-xs text-[#5C5A57] italic">"{r.caption}"</div>}

                <div className="text-xs text-[#5C5A57]">
                  Recebido em {formatDateTime(r.received_at)}
                </div>
                <div className="text-xs border-t border-[#F0EBE5] pt-2">
                  Parcela provavel: {r.probable_installment_id
                    ? <strong>{brl(r.probable_amount)} · venc. {brDate(r.probable_due_date)}</strong>
                    : <span className="text-[#5C5A57]">nenhuma parcela em aberto</span>}
                </div>

                {r.status === "pending" ? (
                  openSettle === r.id ? (
                    <div className="border border-[#EBE5DF] bg-[#FAF7F3] p-3 space-y-2" data-testid={`receipt-settle-${r.id}`}>
                      <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57]">Selecione a parcela</label>
                      <select value={chosen} onChange={(e) => setChosen(e.target.value)} className="w-full text-sm border border-[#EBE5DF] px-2 py-1.5" data-testid={`receipt-installment-select-${r.id}`}>
                        <option value="">Escolher parcela...</option>
                        {installments.map((i) => <option key={i.id} value={i.id}>{brl(i.amount)} · venc. {brDate(i.due_date)} {i.status === "late" ? "(atrasada)" : ""}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <button onClick={() => settle(r)} disabled={busy === r.id} className="brand flex-1" data-testid={`receipt-confirm-settle-${r.id}`}>{busy === r.id ? "..." : "Confirmar baixa"}</button>
                        <button onClick={() => setOpenSettle(null)} className="text-xs tracking-widest uppercase font-medium hover:underline px-3">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-auto">
                      <button onClick={() => openSettleFor(r)} className="brand flex-1" data-testid={`receipt-settle-btn-${r.id}`}>Dar baixa na parcela</button>
                      <button onClick={() => reject(r)} disabled={busy === r.id} className="brand" style={{ background: "#B4564F" }} data-testid={`receipt-reject-btn-${r.id}`}>Rejeitar</button>
                    </div>
                  )
                ) : r.status === "approved" ? (
                  <div className="text-xs text-[#3F6B45] mt-auto" data-testid={`receipt-approved-${r.id}`}>Baixa realizada {r.settled_at ? "em " + formatDateTime(r.settled_at) : ""}</div>
                ) : (
                  <div className="text-xs text-[#B4564F] mt-auto" data-testid={`receipt-rejected-${r.id}`}>Rejeitado {r.rejected_at ? "em " + formatDateTime(r.rejected_at) : ""}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
